import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useEmisores,
  useCrearEmisor,
  useActualizarEmisor,
  useEliminarEmisor
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, estadoColumn } from '../../components/admin/crud';
import { estadoField } from '../../components/admin/crud/CrudForm';
import { z } from 'zod';

import type { Emisor } from '../../types';
import type { CrearEmisor } from '../../services/referenciasService';

const nombreField = {
  name: 'nombre',
  label: 'Nombre',
  type: 'text' as const,
  placeholder: 'Nombre del emisor',
  required: true,
  validation: z.string().min(1, 'Nombre es requerido').max(200, 'Nombre muy largo'),
};

const cargoField = {
  name: 'cargo',
  label: 'Cargo',
  type: 'text' as const,
  placeholder: 'Cargo del emisor (ej. Jefe de Logística)',
  validation: z.string().optional(),
};

export const EmisoresView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('emisores.crear');
  const canEdit = hasPermission('emisores.editar');
  const canDelete = hasPermission('emisores.eliminar');

  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Emisor;
  }>({
    isOpen: false,
    mode: 'create',
  });

  const { data, isLoading, error } = useEmisores(filtros);
  const crearMutation = useCrearEmisor();
  const actualizarMutation = useActualizarEmisor();
  const eliminarMutation = useEliminarEmisor();

  const columns = useMemo(() => [
    { key: 'nombre', title: 'Nombre', width: '240px' },
    { key: 'cargo', title: 'Cargo', width: '240px', render: (v: string) => v || '-' },
    estadoColumn<Emisor>(),
  ], []);

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
  ], [t]);

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({ isOpen: true, mode: 'create' });
  };

  const handleEdit = (emisor: Emisor) => {
    setFormModal({ isOpen: true, mode: 'edit', data: emisor });
  };

  const handleDelete = async (emisor: Emisor) => {
    try {
      await eliminarMutation.mutateAsync(emisor.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando emisor:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearEmisor) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({ id: formModal.data.id, datos });
        toast.success(t('admin.update_success'));
      }
      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario emisor:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button onClick={() => navigate('/admin')} className="hover:text-gray-700 transition-colors">
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.emisores')}</span>
      </div>

      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.emisores')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={[nombreField, cargoField, estadoField]}
        title={formModal.mode === 'create' ? t('admin.emisor_create') : t('admin.emisor_edit')}
        submitText={t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default EmisoresView;
